/* eslint-disable no-console */

// NC1 viewer unlock (AWS-PORT chunk 4): get the account identity into this
// browser's keystore so the Decryptor can open records.
//
// Three ways in, tried in UX order:
//   1. Passkey PRF — silent-ish (one biometric touch): evaluate PRF against
//      the published wraps.webauthn entries, unwrap, cache.
//   2. Link a device (/link-device) — handled by DeviceLink.js; the dialog
//      just routes there.
//   3. Recovery code — unwrap wraps.recovery with the normalized code.
//
// Successful unlocks notify through the EXISTING E2EEPasscodePrompt unlock
// channel — EncryptedImage/EncryptedThumbnail already subscribe to it, so
// every failed tile retries with zero new wiring.

import E2eeApi from '@/aws/e2ee/E2eeApi'
import store from '@/store'
import E2EEPasscodePrompt from '../E2EEPasscodePrompt'
import { getSodium } from './sodium'
import { saveIdentity, loadIdentity } from './Keystore'
import { evaluatePrf, prfProbablySupported } from './PasskeyPrf'
import { unwrapFromWebauthn, unwrapSecrets } from './shared/kek.js'
import { normalizeRecoveryCode } from './shared/recovery.js'
import { invalidateContext } from './Decryptor'

export async function hasCachedIdentity (email) {
  return !!(await loadIdentity(String(email).toLowerCase()))
}

async function fetchBundle (email) {
  const bundle = (await E2eeApi.getIdentityBundle(email)).data
  if (!bundle || !bundle.idSignPk) throw new Error('Account has no NC1 identity.')
  return bundle
}

function verifyAndPersist (email, bundle, secrets) {
  if (secrets.id_sign_pk !== bundle.idSignPk || secrets.id_enc_pk !== bundle.idEncPk) {
    throw new Error('Unwrapped identity does not match the published account keys.')
  }
  return saveIdentity(email, { secrets, altAccountId: bundle.userId, email })
    .then(() => announceUnlocked(email))
}

/**
 * Tell every encrypted tile/dialog that this browser now holds `email`'s
 * identity. A guest's identity opens OTHER accounts' records (§8.5), and the
 * failed tiles filter unlock notifications by the VIEWED accountId — so drop
 * every cached context and notify for each account this user can view, not
 * just their own. Shared by the passkey/recovery unlocks here and by the
 * device-link requester (LinkDevice.vue), so a linked browser's dashboard
 * re-decrypts without a reload.
 */
export function announceUnlocked (email) {
  invalidateContext()
  const viewable = new Set([String(email).toLowerCase()])
  for (const entry of (store.state.account?.emailRecipientFor || [])) {
    const acct = typeof entry === 'string' ? entry : (entry && (entry.accountId || entry.email))
    if (acct) viewable.add(String(acct).toLowerCase())
  }
  for (const acct of viewable) E2EEPasscodePrompt.notifyUnlocked(acct)
}

/**
 * Opt-in era (2026-09-11): does the account have an NC1 identity AT ALL? A
 * report recipient who accepted an invite before setting up encryption has
 * nothing to unlock — the dialog must send them to set it up instead.
 * Returns null when the probe itself failed (offline): callers fall back to
 * the normal unlock options rather than blocking.
 */
export async function accountHasIdentity (email) {
  try {
    const r = await E2eeApi.getAccount(String(email).toLowerCase())
    return !!(r && r.data && r.data.hasIdentity)
  } catch (e) {
    return null
  }
}

/** Does the account have any passkey wraps this browser could try? */
// True only when a passkey unlock can actually be attempted HERE: the account has a passkey
// wrap AND this browser has WebAuthn with a platform authenticator. The second half matters most
// inside the Android/iOS apps' WebViews - an older WebView has no WebAuthn at all, and offering a
// button that can only fail sends people away from the two options that do work there (approve
// from another device, recovery code).
export async function passkeyUnlockAvailable (email) {
  try {
    if (!(await prfProbablySupported())) return false
    const bundle = await fetchBundle(String(email).toLowerCase())
    return Array.isArray(bundle.wraps?.webauthn) && bundle.wraps.webauthn.length > 0
  } catch (e) {
    return false
  }
}

/**
 * Passkey unlock: one WebAuthn get() with PRF evaluation (a user gesture is
 * required — call from a click handler). Throws on failure/cancel.
 */
export async function passkeyUnlock (email) {
  email = String(email).toLowerCase()
  const sodium = await getSodium()
  const bundle = await fetchBundle(email)
  const entries = bundle.wraps?.webauthn
  if (!Array.isArray(entries) || entries.length === 0) {
    throw new Error('No passkey is set up for this account.')
  }
  // All wraps share the account's fixed prfSalt (SignupBootstrap invariant).
  let credentialIdB64, prfOutput
  try {
    ({ credentialIdB64, prfOutput } = await evaluatePrf({
      credentialIdsB64: entries.map((e) => e.credentialId),
      prfSaltB64: entries[0].prfSalt,
    }))
  } catch (e) {
    // NotAllowedError covers both "cancelled" and "none of this account's passkeys is on this
    // device" (a passkey kept in Windows Hello or a hardware key never reaches a phone); the raw
    // browser text for it explains neither.
    if (e && (e.name === 'NotAllowedError' || e.name === 'SecurityError')) {
      const err = new Error("No passkey for this account could be used on this device, or the request was cancelled. Use \"Approve from another device\" or your recovery code instead.")
      err.name = e.name
      throw err
    }
    throw e
  }
  const env = entries.find((e) => e.credentialId === credentialIdB64) || entries[0]
  let secrets
  try {
    secrets = unwrapFromWebauthn(sodium, prfOutput, env)
  } catch (e) {
    throw new Error("This passkey couldn't unlock your keys on this device. Use \"Approve from another device\" or your recovery code instead.")
  }
  await verifyAndPersist(email, bundle, secrets)
  console.log('[nc1] passkey unlock OK')
}

/** Recovery-code unlock. Throws on a bad/typo'd code. */
export async function recoveryUnlock (email, code) {
  email = String(email).toLowerCase()
  const sodium = await getSodium()
  const normalized = normalizeRecoveryCode(code)
  const bundle = await fetchBundle(email)
  if (!bundle.wraps?.recovery) throw new Error('This account has no recovery backstop.')
  let secrets
  try {
    secrets = unwrapSecrets(sodium, normalized, bundle.wraps.recovery)
  } catch (e) {
    throw new Error('That recovery code is not correct for this account.')
  }
  await verifyAndPersist(email, bundle, secrets)
  console.log('[nc1] recovery unlock OK')
}

/**
 * The NC1 twin of E2EEPasscodePrompt: failed decrypts call `request`, the
 * Nc1UnlockDialog subscribes via `onRequest`. Unlock notifications ride the
 * shared E2EEPasscodePrompt.onUnlock channel (see header).
 */
class Nc1UnlockPromptController {
  constructor () {
    this.onRequest = null
    // Fired when the user CLOSES the unlock dialog without unlocking —
    // callers that hid themselves behind it can resurface.
    this.onDismissed = null
    this.pending = new Set()
  }

  dismissed (accountId) {
    this.resolve(accountId)
    if (this.onDismissed) {
      try { this.onDismissed(accountId) } catch (e) { console.log(e) }
    }
  }

  request (accountId, opts = {}) {
    if (!accountId) return
    if (!opts.force && this.pending.has(accountId)) return
    this.pending.add(accountId)
    console.log('[nc1] unlock requested for ' + accountId + (opts.force ? ' (forced)' : ''))
    if (this.onRequest) {
      try { this.onRequest(accountId) } catch (e) { console.log(e) }
    }
  }

  resolve (accountId) {
    if (accountId) this.pending.delete(accountId)
    else this.pending.clear()
  }
}

export const Nc1UnlockPrompt = new Nc1UnlockPromptController()
